import { db } from "./config";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";

// Users Collection
export const createUser = async (userId, userData) => {
  await setDoc(doc(db, "users", userId), {
    email: userData.email,
    name: userData.name,
    idNumber: userData.idNumber,
    address: "",
    neighborhoodDollars: 1000, // Initial amount
    friends: [],
    homeId: null,
    nfcCardId: null,
    createdAt: new Date(),
    photoURL: null,
  });
};

// Homes Collection
export const createHome = async (homeData) => {
  const homeRef = doc(collection(db, "homes"));
  await setDoc(homeRef, {
    address: homeData.address,
    price: homeData.price,
    occupancyLimit: homeData.occupancyLimit,
    features: [],
    ownerId: null,
    status: "available",
    images: homeData.images || [], // Array of image URLs
    imageUrl:
      homeData.imageUrl ||
      (homeData.images && homeData.images.length > 0
        ? homeData.images[0]
        : null), // Keep for backward compatibility
    createdAt: new Date(),
  });
  return homeRef.id;
};

// Features Collection
export const createFeature = async (featureData) => {
  const featureRef = doc(collection(db, "features"));
  await setDoc(featureRef, {
    name: featureData.name,
    price: featureData.price,
    description: featureData.description,
    imageUrl: featureData.imageUrl,
    createdAt: new Date(),
  });
  return featureRef.id;
};

// User Operations
export const updateUserProfile = async (userId, updateData) => {
  await updateDoc(doc(db, "users", userId), updateData);
};

export const getUserProfile = async (userId) => {
  const userDoc = await getDoc(doc(db, "users", userId));
  return userDoc.exists() ? userDoc.data() : null;
};

// Home Operations
export const purchaseHome = async (userId, homeId) => {
  const userRef = doc(db, "users", userId);
  const homeRef = doc(db, "homes", homeId);

  const userDoc = await getDoc(userRef);
  const homeDoc = await getDoc(homeRef);

  if (!userDoc.exists() || !homeDoc.exists()) {
    throw new Error("User or home not found");
  }

  const userData = userDoc.data();
  const homeData = homeDoc.data();

  if (userData.neighborhoodDollars < homeData.price) {
    throw new Error("Insufficient funds");
  }

  await updateDoc(userRef, {
    neighborhoodDollars: userData.neighborhoodDollars - homeData.price,
    homeId: homeId,
    address: homeData.address,
  });

  await updateDoc(homeRef, {
    ownerId: userId,
    status: "occupied",
  });
};

// Friends Operations
export const addFriend = async (userId, friendId) => {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    throw new Error("User not found");
  }

  const userData = userDoc.data();
  const friends = userData.friends || [];

  if (!friends.includes(friendId)) {
    await updateDoc(userRef, {
      friends: [...friends, friendId],
    });
  }
};

export const getFriends = async (userId) => {
  const userDoc = await getDoc(doc(db, "users", userId));
  if (!userDoc.exists()) return [];

  const userData = userDoc.data();
  const friendPromises = (userData.friends || []).map((friendId) =>
    getDoc(doc(db, "users", friendId))
  );

  const friendDocs = await Promise.all(friendPromises);
  return friendDocs
    .filter((doc) => doc.exists())
    .map((doc) => ({ id: doc.id, ...doc.data() }));
};

// Pool Pass Operations
export const buyPoolDayPass = async (userId) => {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) throw new Error("User not found");
  const userData = userDoc.data();
  if ((userData.neighborhoodDollars || 0) < 3)
    throw new Error("Insufficient ND");
  const passes = userData.poolPasses || [];
  // Check for season pass
  if (passes.some((p) => p.type === "season"))
    throw new Error("You already have a season pass.");
  // Check for daily pass today
  const today = new Date().toISOString().slice(0, 10);
  if (
    passes.some(
      (p) => p.type === "day" && (p.purchasedAt || "").slice(0, 10) === today
    )
  ) {
    throw new Error("You already bought a daily pass today.");
  }
  passes.push({
    type: "day",
    purchasedAt: new Date().toISOString(),
    filePath: "/dayPass.png",
  });
  await updateDoc(userRef, {
    neighborhoodDollars: userData.neighborhoodDollars - 3,
    poolPasses: passes,
  });
};

export const buyPoolSeasonPass = async (userId) => {
  const userRef = doc(db, "users", userId);
  const userDoc = await getDoc(userRef);
  if (!userDoc.exists()) throw new Error("User not found");
  const userData = userDoc.data();
  if ((userData.neighborhoodDollars || 0) < 10)
    throw new Error("Insufficient ND");
  const passes = userData.poolPasses || [];
  // Only one season pass allowed
  if (passes.some((p) => p.type === "season"))
    throw new Error("You already have a season pass.");
  passes.push({
    type: "season",
    purchasedAt: new Date().toISOString(),
    filePath: "/seasonPass.png",
  });
  await updateDoc(userRef, {
    neighborhoodDollars: userData.neighborhoodDollars - 10,
    poolPasses: passes,
  });
};

// Pool Chat Operations
export const sendPoolChatMessage = async (
  userId,
  userName,
  userPhoto,
  message
) => {
  await addDoc(collection(db, "poolChat"), {
    userId,
    userName,
    userPhoto,
    message,
    timestamp: serverTimestamp(),
  });
};

export const getPoolChatMessages = (callback) => {
  const q = query(
    collection(db, "poolChat"),
    orderBy("timestamp", "asc"),
    limit(50)
  );
  return onSnapshot(q, (querySnapshot) => {
    const messages = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    callback(messages);
  });
};
